/**
 * O canal de eventos (`GET /api/stream`) virando releitura da mesa.
 *
 * A conexão é UMA e mora em `api.js` (`aoEvento`); aqui só se decide o que cada
 * evento relê. Saiu do `Provedor` por tamanho, não por comportamento: o efeito é
 * o mesmo, com as mesmas dependências.
 */
import { useEffect } from "react";
import * as api from "../api.js";
import { comIdentidadePreservada, costurarAntigas } from "../estado.jsx";
import { comEstadoDoNumero, comListaDeNumeros, ehDoPadrao } from "../whatsapp-estado.js";

/**
 * Qual vista relê quando cada tipo de evento chega.
 *
 * Só entram vistas que `carregarVista` sabe buscar. As que buscam sozinhas
 * (Reuniões, Documentos, Equipe) observam `estado.pulso[tipo]` — mesmo sinal,
 * outro caminho.
 *
 * O evento de `tarefa` é o caso que doía: ele alimenta o sino e o quadro, e só
 * o sino era avisado. A AGENDA saiu daqui quando virou visão de Tarefas: ela
 * pede a própria janela e observa `pulso` (o mesmo sinal, outro caminho), que é
 * também como a reunião marcada volta a aparecer no dia aberto.
 */
const VISTAS_DO_EVENTO = {
  lead: ["avisos"],
  registro: ["avisos", "tarefas"],
  copiloto: ["avisos", "agentes_ia"],
  peca: ["avisos"],
  tarefa: ["avisos", "tarefas"],
  fila: ["avisos"],
  checklist: ["avisos"],
  /* Agente criado/ajustado noutra aba: a vista relê. */
  agente_ia: ["agentes_ia"],
};

/*
 * Os tipos que a VOLTA DO CANAL considera perdidos.
 *
 * Quando o SSE cai, tudo que aconteceu no intervalo aconteceu para ninguém — e
 * a tela não tem como saber O QUE perdeu. Então ela considera tudo perdido:
 * marca cada tipo como sujo e deixa a mesma máquina de releitura dos eventos
 * fazer uma leitura só (`religar`, no efeito do canal).
 *
 * Tirado das chaves dos dois catálogos, e não escrito à mão: tipo novo entra em
 * um deles quando alguém liga um evento na tela, e uma terceira lista aqui seria
 * a que ninguém lembra de atualizar — a vista nova voltaria velha da queda, sem
 * nada na tela dizendo isso.
 */
const TIPOS_DE_EVENTO = [...new Set([...Object.keys(VISTAS_DO_EVENTO), "gravacao", "reuniao", "publicacao"])];

export function useCanalDeEventos(c) {
  const { usuario, carregarMesa, carregarVista, lerCopiloto, listaDaMesa, mudar, navRef, releitura, relogioPresenca } = c;
  useEffect(() => {
    if (!usuario) return undefined;

    /*
     * A RELEITURA DE VOLTA — o conserto de "não atualiza 100%".
     *
     * O SSE é disparo único: quem estava desconectado não recebe depois. Então
     * a volta do canal não é notícia de rede, é notícia de que a tela pode
     * estar velha — e o único jeito honesto de saber o que se perdeu é
     * perguntar de novo.
     *
     * Reusa a MESMA máquina de releitura dos eventos (`releitura.current` +
     * relógio de 180ms): marca a mesa suja, marca o caso aberto e marca todos
     * os tipos, para a vista aberta e os pulsos entrarem junto. Um segundo
     * caminho de recarga seria a segunda verdade sobre "como esta tela se
     * atualiza" — e envelheceria sozinho na primeira vista nova.
     *
     * O `conectado` da PRIMEIRA vez não relê: seria buscar de novo, dois
     * segundos depois, tudo o que a abertura da mesa acabou de buscar. Quem
     * cuida disso é `jaConectou`, no ouvinte — `religar` em si não tem memória,
     * porque a volta da ABA (abaixo) precisa dele já na primeira vez.
     */
    let jaConectou = false;
    /* `onopen` dispara também na PRIMEIRA conexão: só a volta de uma queda relê. */
    let caiuAntes = false;
    const religar = () => {
      releitura.current.mesa = true;
      for (const t of TIPOS_DE_EVENTO) releitura.current.tipos.add(t);
      if (navRef.current.casoId) releitura.current.caso = navRef.current.casoId;
      agendarReleitura();
    };

    const desassinar = api.aoEvento(async (ev) => {
      /*
       * Evento do WhatsApp mexe em UMA tela e em nenhuma mesa: o pareamento
       * (QR novo, conectou, caiu), que se vê em Ajustes. A mensagem em si chega
       * como evento de `lead` — buscar o funil inteiro a cada QR, que troca a
       * cada vinte segundos, seria varrer a mesa por nada.
       */
      if (ev.tipo === "whatsapp") {
        if (ev.acao === "estado" || ev.acao === "conexao") {
          /*
           * O estado entra no `wa` DIRETO do evento, sem ida ao servidor.
           *
           * Duas razões, e a segunda é a que obriga. Ida a mais: o evento já
           * traz palavra, frase e código — buscar de novo é uma requisição para
           * saber o que acabou de chegar. E permissão: `/api/whatsapp/conexao`
           * exige `automacao:ver`, que quem ATENDE não tem — a busca abaixo
           * responde 403, cai no `catch` e vira `null`, então o aviso da mesa
           * nunca chegaria justamente a quem precisa dele. O estado vem com a
           * mesa (`/api/mesa`) e se mantém por aqui.
           */
          /* Cada número na sua linha; o aviso geral (`wa`) só com o padrão —
             ver `comEstadoDoNumero` em whatsapp-estado.js. */
          if (ev.acao === "estado" && ev.estado)
            mudar((atual) => ({ numerosDoSetor: comEstadoDoNumero(atual.numerosDoSetor, ev) }));
          /* Número criado, tirado ou padrão trocado: o evento traz a lista
             inteira, e o `wa` passa a ser o do padrão novo. */
          if (ev.acao === "conexao" && Array.isArray(ev.numeros)) mudar((atual) => comListaDeNumeros(atual, ev.numeros));
          if (ev.acao === "estado" && ev.estado && ehDoPadrao(ev))
            mudar((atual) => ({
              wa: {
                ...atual.wa,
                estado: ev.estado,
                detalhe: ev.detalhe ?? null,
                codigo: ev.codigo ?? null,
                /* O evento não carrega `desde` — quem muda de estado está
                 mudando AGORA, e é isso que o "fora há X" precisa saber. */
                desde: ev.estado === atual.wa?.estado ? (atual.wa?.desde ?? null) : new Date().toISOString(),
              },
            }));
          /* A tela de Ajustes é que precisa do resto (credencial, QR). Quem não
           alcança a automação nem pergunta: eram ~30 recusas 403 por dia na
           tela de quem atende, a cada QR novo do aparelho. */
          const tem = (p) => usuario.permissoes?.includes("*") || usuario.permissoes?.includes(p);
          const [conexaoWa, qr] = await Promise.all([
            tem("automacao:ver") ? api.conexaoWhatsapp().catch(() => null) : null,
            tem("automacao:conexao") ? api.qrWhatsapp().catch(() => null) : null,
          ]);
          mudar({ ...(conexaoWa ? { conexaoWa } : {}), qrWa: qr?.qr ?? null });
        }
        return;
      }
      /*
       * "digitando…" do cliente: troca uma linha, não recarrega o caso.
       *
       * A presença chega várias vezes por minuto enquanto alguém escreve. Cair
       * no caminho de baixo (relê a mesa E o caso aberto) seria buscar o funil
       * inteiro a cada tecla do cliente — e é justamente o dado mais perecível
       * da tela, que não pode custar 120 KB.
       */
      if (ev.tipo === "lead" && ev.acao === "presenca") {
        if (navRef.current.casoId !== ev.id) return;
        mudar((a) => ({ presenca: { ...a.presenca, estado: ev.presenca } }));
        /* "Digitando" não tem fim garantido: o aparelho manda `paused` quando a
         pessoa para, e não manda nada quando ela fecha o WhatsApp. Sem este
         prazo a conversa ficaria "digitando…" para sempre — mentira sobre a
         única coisa que se olha ali. */
        clearTimeout(relogioPresenca.current);
        if (ev.presenca === "composing" || ev.presenca === "recording")
          relogioPresenca.current = setTimeout(() => mudar((a) => ({ presenca: { ...a.presenca, estado: null } })), 12_000);
        return;
      }
      /* "checklist" entra na lista porque agora a tela CONFERE documento: sem ele,
       quem aprovou o holerite via a mudança e o colega ao lado não. */
      /*
       * VIDA DO CANAL — e a volta RELÊ, que é o que faltava.
       *
       * O SSE não tem reprise: o `publish` do servidor é disparo único, sem
       * `Last-Event-ID`, então tudo que acontece com a aba desconectada acontece
       * para ninguém. O `EventSource` reconecta sozinho e a tela apagava a faixa
       * de "reconectando" — voltando ao vivo com o conteúdo de antes da queda e
       * nada dizendo isso.
       *
       * É essa a razão de "a leitura não atualiza 100%": o recibo de leitura
       * chega uma vez, e se chegou no minuto em que o notebook dormiu, o Wi-Fi
       * piscou ou o servidor reiniciou (um deploy é exatamente isso), a bolha
       * fica com um tique até alguém recarregar a página. O mesmo vale para
       * mensagem que entrou, etapa que alguém moveu e documento que chegou.
       *
       * O canal diz QUANDO reler; a verdade continua sendo do servidor. Só relê
       * na VOLTA de uma queda — reler no primeiro `conectado` seria buscar de
       * novo o que a abertura da mesa acabou de buscar.
       */
      if (ev.tipo === "canal") {
        const caiu = ev.acao === "instavel";
        mudar({ canalInstavel: caiu });
        if (caiu) caiuAntes = true;
        else if (caiuAntes) {
          caiuAntes = false;
          religar();
        }
        return;
      }
      /*
       * `conectado` é o sinal HONESTO de que o fluxo está passando.
       *
       * O `onopen` do `EventSession` diz que o socket abriu; este byte chega do
       * outro lado, então prova que o servidor escreveu e a aba leu. Vale como
       * a mesma volta do canal, e é o que fecha o caso do deploy: o processo
       * novo sobe, escreve `conectado`, e a mesa relê sem ninguém apertar nada.
       */
      if (ev.tipo === "conectado") {
        mudar({ canalInstavel: false });
        if (jaConectou) religar();
        jaConectou = true;
        return;
      }
      /*
       * Gravação: só o pulso, e nada de mesa nem de caso.
       *
       * O `id` deste evento é o da GRAVAÇÃO, e não o do registro (ver
       * `src/reunioes.ts`) — cair no caminho de baixo faria `ev.id === aberto`
       * casar por coincidência de número e reler o caso errado. Quem precisa
       * saber que a transcrição terminou é a tela de Reuniões, e ela observa o
       * pulso. A aba Reunião do caso continua sem se atualizar sozinha quando
       * quem gravou foi OUTRA pessoa: para isso o evento teria de carregar o
       * `record_id`, e isso é mudança no barramento.
       */
      if (ev.tipo === "gravacao") {
        mudar((a) => ({ pulso: { ...a.pulso, gravacao: (a.pulso?.gravacao ?? 0) + 1 } }));
        return;
      }
      /* `reuniao` entra porque o `id` dele É o do registro (`src/fluxo.ts`): a
       reunião marcada é item da agenda, e transcrever muda a ficha do caso. */
      /*
       * O ciclo da IA não relê nada: ele PINTA.
       *
       * `passo` chega várias vezes por resposta (e a cada 250ms enquanto o
       * modelo escreve) e não muda uma linha do banco — passar pela rajada de
       * releitura faria a mesa inteira ser pedida de novo por causa de uma
       * animação.
       */
      if (ev.tipo === "copiloto" && ev.acao === "passo") {
        mudar((a) => ({
          iaEmAcao: {
            id: ev.id,
            passo: ev.passo,
            detalhe: ev.detalhe ?? "",
            /* Sem `parcial` no evento o texto de antes fica: os elos entre dois
             pedaços não apagam a frase que já está escrita na tela. */
            parcial: ev.parcial ?? (a.iaEmAcao?.id === ev.id ? (a.iaEmAcao?.parcial ?? "") : ""),
            quando: Date.now(),
          },
        }));
        return;
      }
      /* O resumo automático terminou uma leitura: só o cartão do caso aberto
         relê, e da memória — a mesa não mudou. */
      if (ev.tipo === "copiloto" && ev.acao === "leu") {
        if (ev.id === navRef.current.casoId) void lerCopiloto(ev.id, { modelo: "memoria" });
        return;
      }
      /* O fim do ciclo é passo também, para o palco parar de girar. */
      if (ev.tipo === "copiloto" && ["respondeu", "moveu", "devolvido", "sugeriu"].includes(ev.acao))
        mudar((a) => ({
          iaEmAcao: {
            id: ev.id,
            passo: ev.acao,
            detalhe: ev.detalhe ?? "",
            parcial: a.iaEmAcao?.id === ev.id ? (a.iaEmAcao?.parcial ?? "") : "",
            quando: Date.now(),
          },
        }));
      /* Recibo de entrega (enviado, entregue, lido) só muda o tique da bolha:
         relê o caso ABERTO, se for dele, e nada mais. Era `lead/mensagem` e
         cada tique relia a mesa inteira em toda aba — três por mensagem. */
      if (ev.tipo === "lead" && ev.acao === "entrega") {
        if (ev.id !== navRef.current.casoId) return;
        releitura.current.caso = ev.id;
        agendarReleitura();
        return;
      }
      if (!["lead", "registro", "copiloto", "peca", "tarefa", "fila", "checklist", "reuniao"].includes(ev.tipo)) return;
      /*
       * Uma releitura por rajada, e não uma por evento.
       *
       * Uma mensagem só produz vários eventos em sequência — a mensagem, a
       * leitura, o tique de entrega, o copiloto que acordou. Cada um relia a
       * mesa E o caso aberto (122 KB medidos) e trocava o objeto da conversa
       * inteira: a tela repintava três, quatro vezes seguidas, e é isso que se
       * vê como PISCA-PISCA ao mandar ou receber mensagem.
       *
       * Aqui os eventos da janela viram uma leitura só. A janela é curta de
       * propósito: acima de uns 200ms a mensagem do cliente começa a parecer
       * atrasada, que é o problema que o canal de eventos existe para não ter.
       */
      const aberto = navRef.current.casoId;
      releitura.current.mesa = true;
      /* O tipo do evento é o que decide QUAIS vistas releem — ver
       `VISTAS_DO_EVENTO`. Antes só `avisos` era considerado, e por isso o
       quadro de Tarefas de quem estava olhando ficava congelado enquanto o
       colega arrastava o cartão. */
      releitura.current.tipos.add(ev.tipo);
      /* Guarda QUAL caso reler: o da tela, e só quando o evento é dele (ou da
       fila, que muda o cabeçalho de qualquer caso). */
      /*
       * Caso aberto que foi APAGADO sai da tela na hora, e por aqui de propósito.
       *
       * Quem apaga pode ser esta aba, um colega em outra máquina, ou o segundo
       * clique de um botão que já tinha ido: prender a saída ao retorno da
       * chamada deixava a conversa de um registro inexistente de pé sempre que
       * ela não era a que apagou — a releitura seguinte tomava 404, o `catch`
       * devolvia `null` e a ficha do morto continuava na tela.
       */
      if (ev.tipo === "registro" && ev.acao === "apagado" && ev.id === aberto) {
        navRef.current.voltarParaLista();
        mudar({ caso: null, provas: [] });
      }
      /* Apagado ou arquivado SOME da lista na hora, venha o gesto de quem vier.
       A releitura do topo casa por chave e TROCA — nunca remove (ela só vê a
       primeira página, e apagar o resto esvaziaria a lista de quem rolou).
       Este era o colateral clássico: a ação gravava e a lista ficava velha. */
      if (ev.tipo === "registro" && (ev.acao === "apagado" || ev.acao === "arquivado")) listaDaMesa.remover(ev.id);
      const mexeNoAberto = Boolean(aberto) && (ev.id === aberto || ev.tipo === "fila");
      if (mexeNoAberto) releitura.current.caso = aberto;
      agendarReleitura();
    });

    /*
     * A ABA VOLTANDO — a queda que não avisa que caiu.
     *
     * Celular e notebook CONGELAM a aba em segundo plano: o navegador para de
     * entregar o fluxo sem fechar a conexão, então não vem `onerror`, não vem
     * `instavel` e não vem `conectado`. A pessoa destrava o telefone, olha a
     * conversa e vê o retrato de vinte minutos atrás — com a faixa dizendo que
     * está tudo ao vivo, que é a pior parte.
     *
     * `visibilitychange` é o único sinal que existe para isso, e ele não
     * substitui os outros dois: `conectado` cobre o servidor que reiniciou,
     * `instavel`/`ok` cobrem o Wi-Fi que piscou com a aba na frente, e este
     * cobre o aparelho que dormiu. São três causas, e o mesmo conserto.
     *
     * Sem `jaConectou` na guarda de propósito: a aba pode ser aberta em segundo
     * plano (link tocado no WhatsApp) e ficar velha antes de alguém a ver.
     */
    const aoVoltarParaAba = () => {
      if (document.visibilityState === "visible") religar();
    };
    document.addEventListener("visibilitychange", aoVoltarParaAba);

    /* Duas assinaturas, uma limpeza: sem tirar o ouvinte da aba, cada troca de
     usuário (que refaz este efeito) deixaria um religador vivo — e a décima
     volta à aba pediria a mesa dez vezes. */
    return () => {
      desassinar();
      document.removeEventListener("visibilitychange", aoVoltarParaAba);
      clearTimeout(releitura.current.relogio);
      releitura.current.relogio = null;
    };

    /* O corpo do relógio, extraído para a volta do canal poder agendá-lo também
     — é a mesma releitura, com o mesmo amortecimento. */
    function agendarReleitura() {
      if (releitura.current.relogio) return;
      releitura.current.relogio = setTimeout(async () => {
        const alvo = { ...releitura.current };
        releitura.current = { mesa: false, caso: 0, tipos: new Set(), relogio: null };
        /* Relê a primeira página e casa por chave: o que mudou é trocado no
         lugar, o que é novo entra na frente, e quem rolou não perde o lugar. */
        if (alvo.mesa) await carregarMesa();
        /*
         * A vista ABERTA relê quando o evento é dela.
         *
         * Prazo criado, mensagem lida, copiloto trabalhando: é disto que a tela
         * de Notificações é feita — e o quadro de Tarefas, e a Agenda, pelo mesmo
         * evento de `tarefa`. A mesa já traz a prévia do sino; a lista inteira
         * (e o quadro, e os dias) precisa ser pedida de novo, ou a página fica no
         * retrato da abertura.
         *
         * Só a vista aberta, e só depois da espera: reler a que ninguém está
         * vendo é pagar por payload que vai para o lixo.
         */
        const aqui = navRef.current.vista;
        if ([...alvo.tipos].some((t) => (VISTAS_DO_EVENTO[t] ?? []).includes(aqui))) await carregarVista(aqui);
        /* E o pulso para quem busca sozinha (Reuniões, Documentos, Equipe): elas
         não passam por `carregarVista` e observam este contador. */
        if (alvo.tipos.size)
          mudar((a) => {
            const pulso = { ...a.pulso };
            for (const t of alvo.tipos) pulso[t] = (pulso[t] ?? 0) + 1;
            return { pulso };
          });
        /* E só se a conversa aberta ainda for a mesma: a releitura atrasada de
         um caso que a pessoa deixou pintaria a conversa errada. */
        if (alvo.caso && navRef.current.casoId === alvo.caso) {
          const caso = await api.caso(alvo.caso).catch(() => null);
          if (caso && navRef.current.casoId === alvo.caso) {
            /* As bolhas que voltaram iguais continuam as MESMAS: sem isto o
               `memo(Bolha)` falhava em todo evento e as fotos piscavam. */
            mudar((a) => ({
              caso: comIdentidadePreservada(a.caso, caso, false),
              provas: caso.provas,
              antigas: costurarAntigas(a.antigas, a.caso, caso),
            }));
            /* Mensagem que chega com a conversa NA TELA já foi lida: sem isto o
               selo acendia na lista para o caso aberto e só saía recarregando. */
            if (document.visibilityState === "visible" && caso.mensagens?.some((m) => m.direcao === "entrada" && !m.lida_em))
              api.marcarLida(alvo.caso).catch(() => {});
          }
          /* E a leitura do copiloto junto. Era pedida SÓ na abertura do caso:
           chegava mensagem, a conversa crescia embaixo e o cartão preto
           continuava afirmando o que era verdade dez minutos antes — "só
           saudação, nenhum relato" com o relato já na tela. Sem `await`: a
           leitura do assistente leva dezenas de segundos e não pode segurar a
           próxima rajada de eventos.
           SÓ A MEMÓRIA, nunca o modelo: aqui eram duas chamadas pagas por
           mensagem enquanto o caso estava aberto. A leitura nova vem do resumo
           automático (espera e teto), que avisa com `copiloto/leu`. */
          void lerCopiloto(alvo.caso, { modelo: "memoria" });
        }
      }, 180);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `listaDaMesa.remover` é estável (useCallback) e listá-la refaria a assinatura do SSE a cada página carregada
  }, [carregarMesa, carregarVista, usuario, lerCopiloto, mudar]);
}
